import { float, normalize } from 'three/tsl';
import type { AtmosphereDef } from '../../engine/atmosphere/AtmosphereDef';
import type { CloudDef } from '../../engine/atmosphere/CloudLayer';
import type { TerrainProfile } from '../../engine/terrain/TerrainProfile';
import { VERDANT_STYLE } from '../../engine/terrain/TerrainStyle';
import { VERDANT_GRASS, VERDANT_TREES } from '../../engine/vegetation/VegetationStyle';
import type { HollowSceneDef } from '../scenes/HollowScene';
import { boulders } from './rocks';

/**
 * Titan: a hollow 2,000 km across, the "near-planet size" end of the range. At this
 * scale the ground near you looks like an ordinary world with a hazy horizon, and
 * only above the haze does the land keep rising into the sky: continents, seas and
 * mountain ranges hang overhead 2,000 km away. It's also the engine's precision
 * test: positions are 64-bit, terrain noise is lattice-offset per octave, and
 * rendering is camera-relative, so the ground at your feet stays exact.
 */
export const TITAN_TERRAIN: TerrainProfile = {
  continents: { wavelength: 700_000, octaves: 7, lacunarity: 2.03, gain: 0.52 },
  continentHeight: 1800,
  landBias: 0.12,
  mountainMask: { wavelength: 450_000, octaves: 3, lacunarity: 2.1, gain: 0.5 },
  ridges: { wavelength: 140_000, octaves: 12, lacunarity: 2.01, gain: 0.52 },
  ridgeHeight: 4200,
  ridgeDamping: 1.6,
  hills: { wavelength: 9000, octaves: 6, lacunarity: 2.05, gain: 0.5 },
  hillHeight: 220,
  detail: { wavelength: 150, octaves: 6, lacunarity: 2.07, gain: 0.48 },
  detailHeight: 5,
  snowLine: 3200,
};

/** Earth-like air: a real horizon haze, and a clear core so the far side shows. */
export const TITAN_AIR: AtmosphereDef = {
  rayleigh: [5.8e-6, 13.5e-6, 33.1e-6],
  rayleighHeight: 8000,
  mie: 3e-6,
  mieHeight: 1200,
  mieAbsorption: 0.1,
  mieG: 0.78,
  ambientScatter: 0.6,
  scatterBoost: 5,
};

export const TITAN_CLOUDS: CloudDef = {
  altitude: 3500,
  scale: 60_000,
  coverage: 0.45,
  drift: 0.0002,
};

export const TITAN: HollowSceneDef = {
  id: 'titan',
  name: 'Titan',
  center: [3.3e6, -7.9e6, 5.2e6],
  radius: 1_000_000,
  seed: 71,
  sun: { radius: 14_000, color: [1.0, 0.92, 0.8], intensity: 5.5 },
  terrain: TITAN_TERRAIN,
  air: TITAN_AIR,
  clouds: TITAN_CLOUDS,
  veil: {
    litFraction: 0.46,
    softness: 0.07,
    dayLength: 900,
    spinAxis: [0.1, 1, 0.3],
    duskColor: [1.0, 0.44, 0.2],
    nightAmbient: [0.8, 0.95, 1.4],
  },
  worldAlbedo: [0.15, 0.18, 0.17],
  style: () => VERDANT_STYLE,
  // Overhead a pixel spans about 3 km: the palette's noise (11 m to 3.8 km) fades to its mean.
  filterPalette: true,
  grass: () => VERDANT_GRASS,
  trees: () => VERDANT_TREES,
  rocks: () =>
    boulders(
      [
        [0.36, 0.34, 0.31],
        [0.5, 0.47, 0.42],
      ],
      [
        [0.24, 0.28, 0.19],
        [0.33, 0.35, 0.27],
      ],
    ),
  motes: (rig) => ({
    count: 1200,
    radius: 35,
    size: [0.07, 0.13],
    colors: [
      [0.85, 1.0, 0.35],
      [1.0, 0.85, 0.3],
    ],
    brightness: 5,
    drift: 0.2,
    glow: () =>
      float(1)
        .sub(rig.veilAt(normalize(rig.centerRel.negate())))
        .pow(2),
  }),
  spawn: { dir: [-0.2, 0.9, 0.35], minHeight: 30, maxHeight: 600 },
  exposureKey: 0.42,
  waterSkyTint: [0.8, 0.95, 1.3],
  hints: [
    'This hollow is 2,000 km across. Look up past the haze.',
    'V to fly: climb high and watch the horizon curve up into the sky.',
  ],
  ambience: {
    wind: { gain: 0.9, cutoff: 650 },
    events: [
      { kind: 'bird', rate: 0.3, gain: 0.045, when: 'day' },
      { kind: 'cricket', rate: 1.4, gain: 0.018, when: 'night' },
    ],
  },
};
